<template>
  <div class="min-h-screen bg-gray-50">
    <!-- ヘッダー -->
    <header class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <h1 class="text-2xl font-bold text-gray-900">
              Soarimetric
            </h1>
          </div>
          <nav class="flex items-center space-x-4">
            <router-link to="/areas" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              エリア設定
            </router-link>
            <router-link to="/account" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              アカウント
            </router-link>
            <button @click="handleLogout" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              ログアウト
            </button>
          </nav>
        </div>
      </div>
    </header>

    <main class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <!-- ユーザー情報 -->
      <div class="px-4 py-6 sm:px-0">
        <div class="bg-blue-50 overflow-hidden shadow-lg rounded-lg mb-6 border border-blue-200">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-lg leading-6 font-medium text-gray-900">
                  こんにちは、{{ dashboardData?.user?.email }}さん
                </h2>
                <p class="mt-1 text-sm text-gray-500">
                  全機能無料でご利用いただけます
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- エリアがない場合 -->
        <div v-if="!dashboardData?.areas?.length" class="text-center py-12">
          <div class="text-6xl mb-4">📍</div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            まずはエリアを登録しましょう
          </h3>
          <p class="text-gray-500 mb-6">
            気象情報を確認したいフライトエリアを最大3つまで登録できます
          </p>
          <router-link 
            to="/areas"
            class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            エリアを登録する
          </router-link>
        </div>

        <!-- エリア別気象情報 -->
        <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div 
            v-for="area in dashboardData.areas" 
            :key="area.user_area.id"
            class="bg-green-50 overflow-hidden shadow-lg rounded-lg border border-green-200"
          >
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <div class="text-3xl">
                    {{ getWeatherIcon(area.current_weather?.surface?.weather_code) }}
                  </div>
                </div>
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate">
                      {{ area.user_area.area_name }}
                    </dt>
                    <dd class="text-lg font-medium text-gray-900">
                      <span v-if="area.current_weather">
                        {{ Math.round(area.current_weather.surface.temperature) }}°C
                      </span>
                      <span v-else>データなし</span>
                    </dd>
                  </dl>
                </div>
              </div>
              
              <div v-if="area.current_weather" class="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <dt class="text-xs text-gray-500">風速</dt>
                  <dd class="text-sm font-medium">
                    {{ Math.round(area.current_weather.surface.wind_speed) }} m/s
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">湿度</dt>
                  <dd class="text-sm font-medium">
                    {{ area.current_weather.surface.relative_humidity || 'N/A' }}%
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">雲量</dt>
                  <dd class="text-sm font-medium">
                    {{ area.current_weather.surface.cloud_cover }}%
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">降水確率</dt>
                  <dd class="text-sm font-medium">
                    {{ area.current_weather.surface.precipitation_probability != null ? area.current_weather.surface.precipitation_probability + '%' : '-' }}
                  </dd>
                </div>
              </div>

              <div class="mt-4">
                <router-link 
                  :to="`/weather-detail/${area.user_area.flight_area_id}`"
                  class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-center block"
                >
                  詳細気象データを見る
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 免責事項 -->
    <footer class="bg-gray-800 text-white py-8 mt-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center text-sm text-gray-300 space-y-2">
          <p>本サービスはOpen-Meteo APIから取得した海外の気象データを表示するものです。</p>
          <p>これは気象予報ではありません。データには誤差が含まれる可能性があります。</p>
          <p>正式な気象情報は気象庁の発表をご確認ください。</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useWeatherStore } from '@/stores/weather'

export default {
  name: 'Dashboard',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const weatherStore = useWeatherStore()
    
    const dashboardData = ref(null)
    const isLoading = ref(false)

    const loadDashboard = async () => {
      isLoading.value = true
      try {
        const data = await weatherStore.fetchDashboard()
        dashboardData.value = data
      } catch (error) {
        console.error('Dashboard data fetch failed:', error)
      } finally {
        isLoading.value = false
      }
    }

    const handleLogout = async () => {
      await authStore.logout()
      router.push('/')
    }

    const getWeatherIcon = (code) => {
      if (code <= 3) return '☀️'
      if (code <= 48) return '☁️'
      if (code <= 67) return '🌧️'
      if (code <= 82) return '🌦️'
      return '🌩️'
    }

    // 独自の気象判定は気象業務法に抵触するため削除

    onMounted(() => {
      loadDashboard()
    })

    return {
      dashboardData,
      isLoading,
      handleLogout,
      getWeatherIcon
    }
  }
}
</script>